import { useRef } from 'react'
import type { CSSProperties, InputHTMLAttributes, Ref, TextareaHTMLAttributes } from 'react'
import './textField.css'
import { isTextLike, useTrailingAffordances } from './trailingAffordances'

/**
 * THE text input. Every editable text surface in the estate goes through here — the viewer's
 * sign-in, the signup funnel, and every field on the three staff portals — so the trailing
 * affordances (clear X, reveal eye, number stepper) reach all of them at once.
 * David, 2026-08-04: *"all text inputs need a clear input X at their right edge"*.
 *
 * ★ Only the RAIL is this component's own look. The field's appearance still comes from the
 * caller's `className` / `style`, which is why one component serves the three idioms already in
 * use: Tailwind utilities (viewer, signup), a hand-written stylesheet (`/tvlink`), and inline
 * style objects (the portals).
 *
 * ⚠ THE WRAPPER IS THE LAYOUT ITEM, NOT THE INPUT. The rail is absolutely positioned, which needs
 * a positioned ancestor, which means a wrapper — and the moment a wrapper exists it, not the
 * input, is the flex/grid child. `className`/`style` still go to the INPUT (that is where every
 * existing call site expects them, and typography must reach the text); pass `wrapperClassName` /
 * `wrapperStyle` for anything that positions the field in its parent.
 *
 * ⚠ LAYOUT PROPERTIES ARE HOISTED OFF `style` AUTOMATICALLY (see `LAYOUT_KEYS`) — width, flex,
 * margins and grid placement belong to the wrapper now, and a caller that leaves them in `style`
 * would otherwise get a field shrunk to its intrinsic width, or a rail off centre by half a
 * margin. That is what keeps `<input style={…}>` → `<TextField style={…}>` a drop-in.
 *
 * ⚠ The caller's `style` is merged HERE, not left to the `{...props}` spread — the spread lands
 * last and would replace the whole object, silently taking the reserved gutter with it and
 * letting the value paint under the icons. (Monza measured exactly that: 12px instead of 28px.)
 */
type Props = Omit<InputHTMLAttributes<HTMLInputElement>, 'ref'> & {
  ref?: Ref<HTMLInputElement>
  wrapperClassName?: string
  wrapperStyle?: CSSProperties
  /**
   * ★ Offer a COPY button on the rail. Opt-in, off everywhere it is not asked for, and the only
   * affordance that also works on a `readOnly` field — which is exactly the field that holds
   * something you are meant to take elsewhere.
   */
  copyable?: boolean
}

/**
 * Style properties that describe the field's PLACE IN ITS PARENT rather than its own look. They
 * are moved to the wrapper automatically, because the wrapper — not the input — is now the
 * flex/grid item.
 *
 * ⛔ THIS IS WHAT MAKES `<input style={…}>` → `<TextField style={…}>` A DROP-IN. Leave `width` or
 * `flex` on the inner input and the wrapper sizes to the input's INTRINSIC width instead: every
 * full-width field on the admin portal silently shrinks to the browser's default ~150px. Leave a
 * `margin` there and the wrapper grows taller than the box, which drifts the rail off centre by
 * half the margin.
 *
 * ⚠ It cannot see layout expressed as a CLASS (`w-full`, `flex-1`). Those call sites pass
 * `wrapperClassName` instead — there are none in the estate today, which is why this list is
 * style-only.
 */
const LAYOUT_KEYS = [
  'width',
  'minWidth',
  'maxWidth',
  'flex',
  'flexGrow',
  'flexShrink',
  'flexBasis',
  'alignSelf',
  'justifySelf',
  'gridColumn',
  'gridRow',
  'gridArea',
  'margin',
  'marginTop',
  'marginRight',
  'marginBottom',
  'marginLeft',
  'marginBlock',
  'marginInline',
] as const

const SIZE_KEYS = new Set(['width', 'flex', 'flexGrow', 'flexBasis'])

/** Split a caller's style into [what stays on the input, what moves to the wrapper]. */
function hoistLayout(style: CSSProperties | undefined): [CSSProperties, CSSProperties] {
  if (!style) return [{}, {}]
  const own: Record<string, unknown> = { ...style }
  const wrapper: Record<string, unknown> = {}
  let sized = false
  for (const k of LAYOUT_KEYS) {
    if (own[k] !== undefined) {
      wrapper[k] = own[k]
      delete own[k]
      if (SIZE_KEYS.has(k)) sized = true
    }
  }
  // The input then fills the box the wrapper now owns.
  if (sized) own.width = '100%'
  return [own as CSSProperties, wrapper as CSSProperties]
}

export function TextField({
  className,
  style,
  type,
  wrapperClassName,
  wrapperStyle,
  onFocus,
  onBlur,
  onKeyDown,
  onKeyUp,
  ref,
  copyable,
  ...props
}: Props) {
  const inner = useRef<HTMLInputElement>(null)
  const [ownStyle, hoisted] = hoistLayout(style)
  // A disabled or read-only field has nothing to offer; a non-text type either paints its own
  // trailing control (date, time, color) or has no text to clear.
  const enabled = isTextLike(type) && !props.disabled && !props.readOnly
  // ⛔ COPY OUTLIVES `enabled`. Everything else in the rail edits the value, so a read-only field
  //   switches the rail off entirely — and a read-only field is precisely where a copy button is
  //   wanted. Anything the rail renders needs its lane reserved, hence the padding below.
  const wantsCopy = copyable === true && isTextLike(type) && props.disabled !== true
  // ⚠ The key handlers are COMPOSED here, like focus/blur, rather than left to `{...props}`:
  //   the spread lands last, so wiring them any other way silently drops one side or the other.
  const trailing = useTrailingAffordances(inner, { enabled, type, onFocus, onBlur, onKeyDown, onKeyUp, copyable: wantsCopy })

  return (
    <span
      className={wrapperClassName}
      style={{ position: 'relative', display: 'flex', alignItems: 'stretch', ...hoisted, ...wrapperStyle }}
    >
      <input
        {...props}
        ref={(el) => {
          inner.current = el
          if (typeof ref === 'function') ref(el)
          else if (ref) (ref as { current: HTMLInputElement | null }).current = el
        }}
        type={trailing.effectiveType}
        // `yfx-field` carries ONLY the pseudo-element silencing in textField.css — the browser's
        // own spinner / reveal / search-clear, which would paint inside our rail.
        className={className ? `${className} yfx-field` : 'yfx-field'}
        onFocus={trailing.onFocus}
        onBlur={trailing.onBlur}
        onKeyDown={trailing.onKeyDown}
        onKeyUp={trailing.onKeyUp}
        // Reserved ALWAYS, not only while a button shows: pad on demand instead and the text
        // reflows the instant the rail appears, which is a very visible jump.
        style={{ flex: '1 1 auto', minWidth: 0, ...ownStyle, ...(enabled || wantsCopy ? { paddingRight: trailing.gutter } : null) }}
      />
      {trailing.rail}
    </span>
  )
}

/** Height of a textarea's rail — one field-row, so the X sits on the first line of text. */
const TEXTAREA_RAIL_H = 34

/** A password field. Reveal + clear; the eye is left of the X, which stays right-most. */
export function PasswordField(props: Omit<Props, 'type'>) {
  return <TextField {...props} type="password" />
}

/** The same clear, on a textarea. No reveal, no stepper — there is nothing to reveal or step. */
export function TextArea({
  className,
  style,
  wrapperClassName,
  wrapperStyle,
  onFocus,
  onBlur,
  onKeyDown,
  onKeyUp,
  ref,
  ...props
}: Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'ref'> & {
  ref?: Ref<HTMLTextAreaElement>
  wrapperClassName?: string
  wrapperStyle?: CSSProperties
}) {
  const inner = useRef<HTMLTextAreaElement>(null)
  const [ownStyle, hoisted] = hoistLayout(style)
  const enabled = !props.disabled && !props.readOnly
  const trailing = useTrailingAffordances(inner, {
    enabled,
    type: 'text',
    onFocus,
    onBlur,
    onKeyDown,
    onKeyUp,
    // ⚠ Pinned to the first row: a textarea grows downwards, and an X at the vertical middle of a
    //   tall box reads as belonging to nothing.
    railHeight: TEXTAREA_RAIL_H,
  })

  return (
    <span
      className={wrapperClassName}
      style={{ position: 'relative', display: 'flex', alignItems: 'stretch', ...hoisted, ...wrapperStyle }}
    >
      <textarea
        {...props}
        ref={(el) => {
          inner.current = el
          if (typeof ref === 'function') ref(el)
          else if (ref) (ref as { current: HTMLTextAreaElement | null }).current = el
        }}
        className={className ? `${className} yfx-field` : 'yfx-field'}
        onFocus={trailing.onFocus}
        onBlur={trailing.onBlur}
        onKeyDown={trailing.onKeyDown}
        onKeyUp={trailing.onKeyUp}
        style={{ flex: '1 1 auto', minWidth: 0, ...ownStyle, ...(enabled ? { paddingRight: trailing.gutter } : null) }}
      />
      {trailing.rail}
    </span>
  )
}

export default TextField
